import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './CSS/SearchPage.css';
import { useProducts, useSearchProducts } from '../hooks/useProducts';
import { useCategories } from '../hooks/useCategories';
import Item from '../componnents/Item/item';
import { ProductSkeleton, EmptyState } from '../componnents/UI';
import {
  getProductImageUrl,
  getProductId,
  getProductName,
  getProductPrices,
  formatPrice,
  isProductInStock,
} from '../utils/helpers';
import { 
  Filter, X, ChevronDown, ChevronUp, SlidersHorizontal, 
  Grid3X3, List, Star, Heart, ShoppingCart, Loader2
} from 'lucide-react';
import { apiService } from '../services/api';
import { translateProducts } from '../utils/translateData';

const SearchPage = () => {
  const { t } = useTranslation(['search', 'common']);
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get initial values from URL
  // Support both 'q' and 'search' parameters for compatibility
  const searchQuery = searchParams.get('q') || searchParams.get('search') || '';
  const initialCategoryParam = searchParams.get('category') || '';
  const initialMinPrice = searchParams.get('minPrice') || '';
  const initialMaxPrice = searchParams.get('maxPrice') || '';
  const initialSort = searchParams.get('sort') || 'newest';
  const initialRating = searchParams.get('rating') || '';

  // Fetch categories
  const { data: categories, isLoading: isLoadingCategories } = useCategories();

  // Local state
  const [selectedCategory, setSelectedCategory] = useState('');
  const [minPrice, setMinPrice] = useState(initialMinPrice);
  const [maxPrice, setMaxPrice] = useState(initialMaxPrice);
  const [minRating, setMinRating] = useState(initialRating);
  const [sortBy, setSortBy] = useState(initialSort);
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [expandedSections, setExpandedSections] = useState({
    categories: false,
    price: true,
    rating: false,
  });

  // Initialize category from URL when categories are loaded
  useEffect(() => {
    if (categories && initialCategoryParam && !selectedCategory) {
      // Convert category ID to name if needed (for URL compatibility)
      let categoryName = initialCategoryParam;
      // If it's an ID (MongoDB ObjectId format), find the category name
      if (initialCategoryParam.length === 24 && /^[0-9a-fA-F]{24}$/.test(initialCategoryParam)) {
        const category = categories.find(c => (c._id || c.id) === initialCategoryParam);
        categoryName = category?.name || '';
      }
      if (categoryName) {
        setSelectedCategory(categoryName);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, initialCategoryParam]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [allProducts, setAllProducts] = useState([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  // Build filter params
  const filterParams = {
    ...(selectedCategory && { category: selectedCategory }),
    ...(minPrice && { minPrice: Number(minPrice) }),
    ...(maxPrice && { maxPrice: Number(maxPrice) }),
    ...(minRating && { minRating: Number(minRating) }),
    ...(sortBy && { sort: sortBy }),
    limit: 20,
    page: 1,
  };

  // Fetch products based on search or filters
  const searchResult = useSearchProducts(searchQuery, filterParams);
  const allProductsResult = useProducts(filterParams);
  
  // Use search results if there's a query, otherwise use filtered products
  const { data, isLoading, error } = searchQuery ? searchResult : allProductsResult;
  
  // Update products when data changes
  useEffect(() => {
    if (data) {
      const newProducts = Array.isArray(data.products) ? data.products : [];
      if (currentPage === 1) {
        setAllProducts(newProducts);
      }
      
      // Check if there are more products
      const pagination = data.pagination;
      const total = data.total || 0;
      const currentTotal = currentPage === 1 ? newProducts.length : allProducts.length + newProducts.length;
      setHasMore(pagination?.hasMore !== false && (pagination?.hasMore || currentTotal < total || newProducts.length >= 20));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);
  
  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
    setAllProducts([]);
    setHasMore(true);
  }, [selectedCategory, minPrice, maxPrice, minRating, sortBy, searchQuery]);
  
  // Ensure products is always an array
  const products = allProducts.length > 0 ? allProducts : (Array.isArray(data?.products) ? data.products : []);

  // Update URL when filters change (keep the search query)
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchQuery) params.set('q', searchQuery);
    if (selectedCategory) params.set('category', selectedCategory);
    if (minPrice) params.set('minPrice', minPrice);
    if (maxPrice) params.set('maxPrice', maxPrice);
    if (minRating) params.set('rating', minRating);
    if (sortBy !== 'newest') params.set('sort', sortBy);
    
    setSearchParams(params, { replace: true });
  }, [searchQuery, selectedCategory, minPrice, maxPrice, minRating, sortBy, setSearchParams]);

  const clearFilters = () => {
    setSelectedCategory('');
    setMinPrice('');
    setMaxPrice('');
    setMinRating('');
    setSortBy('newest');
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const hasActiveFilters = selectedCategory || minPrice || maxPrice || minRating || sortBy !== 'newest';

  // Price range presets
  const pricePresets = [
    { label: t('pricePresets.under25'), min: '', max: '25' },
    { label: t('pricePresets.25to50'), min: '25', max: '50' },
    { label: t('pricePresets.50to100'), min: '50', max: '100' },
    { label: t('pricePresets.100to200'), min: '100', max: '200' },
    { label: t('pricePresets.over200'), min: '200', max: '' },
  ];

  const ratingOptions = [
    { value: '4', label: t('ratingOptions.4up') },
    { value: '3', label: t('ratingOptions.3up') },
    { value: '2', label: t('ratingOptions.2up') },
  ];

  const sortOptions = [
    { value: 'newest', label: t('sortOptions.newest') },
    { value: 'popular', label: t('sortOptions.popular') },
    { value: 'price_asc', label: t('sortOptions.price_asc') },
    { value: 'price_desc', label: t('sortOptions.price_desc') },
    { value: 'rating', label: t('sortOptions.rating') },
  ];

  return (
    <div className="search-page page-shell page-shell--muted">
      <div className="search-content">
        {/* Filters Sidebar */}
        <aside className={`filters-sidebar ${showFilters ? 'open' : ''}`}>
          <div className="filters-header">
            <h3>
              <Filter size={18} />
              {t('filters')}
            </h3>
            {hasActiveFilters && (
              <button className="clear-all-btn" onClick={clearFilters}>
                {t('clearAll')}
              </button>
            )}
            <button className="close-filters" onClick={() => setShowFilters(false)}>
              <X size={24} />
            </button>
          </div>

          {/* Categories Filter */}
          <div className="filter-section">
            <button className="filter-section-header" onClick={() => toggleSection('categories')}>
              <span>{t('categories')}</span>
              {expandedSections.categories ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>
            {expandedSections.categories && (
              <div className="filter-section-content">
                <label className={`filter-option ${selectedCategory === '' ? 'active' : ''}`}>
                  <input
                    type="radio"
                    name="category"
                    checked={selectedCategory === ''}
                    onChange={() => setSelectedCategory('')}
                  />
                  <span>{t('allCategories')}</span>
                </label>
                {isLoadingCategories ? (
                  <div className="filter-loading">{t('loading')}</div>
                ) : (
                  categories?.map((cat) => (
                    <label 
                      key={cat._id || cat.id} 
                      className={`filter-option ${selectedCategory === cat.name ? 'active' : ''}`}
                    >
                      <input
                        type="radio"
                        name="category"
                        checked={selectedCategory === cat.name}
                        onChange={() => setSelectedCategory(cat.name)}
                      />
                      <span>{cat.name}</span>
                      {cat.productCount && (
                        <span className="filter-count">({cat.productCount})</span>
                      )}
                    </label>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Price Range Filter */}
          <div className="filter-section">
            <button className="filter-section-header" onClick={() => toggleSection('price')}>
              <span>{t('priceRange')}</span>
              {expandedSections.price ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>
            {expandedSections.price && (
              <div className="filter-section-content">
                <div className="price-inputs">
                  <div className="price-input-group">
                    <label>{t('min')}</label>
                    <div className="price-input-wrapper">
                      <span className="currency">$</span>
                      <input
                        type="number"
                        value={minPrice}
                        onChange={(e) => setMinPrice(e.target.value)}
                        placeholder="0"
                        min="0"
                      />
                    </div>
                  </div>
                  <span className="price-separator">–</span>
                  <div className="price-input-group">
                    <label>{t('max')}</label>
                    <div className="price-input-wrapper">
                      <span className="currency">$</span>
                      <input
                        type="number"
                        value={maxPrice}
                        onChange={(e) => setMaxPrice(e.target.value)}
                        placeholder={t('any')}
                        min="0"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="price-presets">
                  {pricePresets.map((preset, idx) => (
                    <button
                      key={idx}
                      className={`price-preset ${minPrice === preset.min && maxPrice === preset.max ? 'active' : ''}`}
                      onClick={() => {
                        setMinPrice(preset.min);
                        setMaxPrice(preset.max);
                      }}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Rating Filter */}
          <div className="filter-section">
            <button className="filter-section-header" onClick={() => toggleSection('rating')}>
              <span>{t('customerRating')}</span>
              {expandedSections.rating ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>
            {expandedSections.rating && (
              <div className="filter-section-content">
                {ratingOptions.map((option) => (
                  <label 
                    key={option.value} 
                    className={`filter-option rating-option ${minRating === option.value ? 'active' : ''}`}
                  >
                    <input
                      type="radio"
                      name="rating"
                      checked={minRating === option.value}
                      onChange={() => setMinRating(minRating === option.value ? '' : option.value)}
                    />
                    <div className="rating-stars">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          size={14}
                          fill={i < Number(option.value) ? '#fbbf24' : 'none'}
                          stroke={i < Number(option.value) ? '#fbbf24' : '#d1d5db'}
                        />
                      ))}
                    </div>
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Apply Filters Button (Mobile) */}
          <button className="apply-filters-btn" onClick={() => setShowFilters(false)}>
            {t('showResults', { count: products.length })}
          </button>
        </aside>

        {/* Products Main */}
        <main className="products-main">
          {/* Results Header */}
          <div className="results-header">
            <div className="results-info">
              {searchQuery && (
                <h1 className="search-query-text">
                  {t('resultsFor')} "<span>{searchQuery}</span>"
                </h1>
              )}
            </div>
            
            <div className="results-controls">
              {/* Mobile Filter Toggle */}
              <button 
                className={`filter-toggle ${showFilters ? 'active' : ''}`}
                onClick={() => setShowFilters(!showFilters)}
              >
                <SlidersHorizontal size={18} />
                <span>{t('filters')}</span>
                {hasActiveFilters && <span className="filter-badge"></span>}
              </button>

              {/* View Toggle */}
              <div className="view-toggle">
                <button 
                  className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                  onClick={() => setViewMode('grid')}
                  title={t('gridView')}
                >
                  <Grid3X3 size={18} />
                </button>
                <button 
                  className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
                  onClick={() => setViewMode('list')}
                  title={t('listView')}
                >
                  <List size={18} />
                </button>
              </div>

              {/* Sort Dropdown */}
              <div className="sort-wrapper">
                <label>{t('sort')}:</label>
                <select 
                  value={sortBy} 
                  onChange={(e) => setSortBy(e.target.value)}
                  className="sort-select"
                >
                  {sortOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Active Filters Tags */}
          {hasActiveFilters && (
            <div className="active-filters">
              {selectedCategory && (
                <span className="filter-tag">
                  <span className="tag-label">{t('category')}:</span>
                  {selectedCategory}
                  <button onClick={() => setSelectedCategory('')}><X size={14} /></button>
                </span>
              )}
              {(minPrice || maxPrice) && (
                <span className="filter-tag">
                  <span className="tag-label">{t('price')}:</span>
                  ${minPrice || '0'} – ${maxPrice || t('any')}
                  <button onClick={() => { setMinPrice(''); setMaxPrice(''); }}><X size={14} /></button>
                </span>
              )}
              {minRating && (
                <span className="filter-tag">
                  <span className="tag-label">{t('rating')}:</span>
                  {minRating}★ & Up
                  <button onClick={() => setMinRating('')}><X size={14} /></button>
                </span>
              )}
            </div>
          )}

          {/* Products Grid/List */}
          {isLoading ? (
            <div className={`products-${viewMode}`}>
              <ProductSkeleton count={12} />
            </div>
          ) : error ? (
            <div className="search-error">
              <p>{t('failedToLoad')}</p>
            </div>
          ) : products.length === 0 ? (
            <EmptyState
              icon="search"
              title={t('noResults')}
              description={searchQuery 
                ? t('noResultsDescription')
                : t('noResultsNoFilters')
              }
              actionText={t('clearFilters')}
              onAction={clearFilters}
            />
          ) : (
            <div className={`products-${viewMode}`}>
              {products.map((item, i) => {
                const { price, comparePrice, fromPrice } = getProductPrices(item);
                return viewMode === 'grid' ? (
                  <Item
                    key={getProductId(item) || i}
                    id={getProductId(item)}
                    image={getProductImageUrl(item)}
                    name={getProductName(item)}
                    newPrice={price}
                    oldPrice={comparePrice}
                    fromPrice={fromPrice}
                    sold={item.sold || item.salesCount || 0}
                    rating={item.averageRating || item.rating || 4.5}
                    shipping={item.freeShipping ? t('freeShipping', { ns: 'common' }) : t('standardShipping', { ns: 'common' })}
                    inStock={isProductInStock(item)}
                    colors={item.colors || item.variants?.map(v => v.color).filter(Boolean)}
                  />
                ) : (
                  <ProductListItem
                    key={getProductId(item) || i}
                    product={item}
                  />
                );
              })}
            </div>
          )}
          
          {/* Load More Button */}
          {hasMore && products.length > 0 && (
            <div className="load-more-container" style={{ textAlign: 'center', marginTop: '2rem', padding: '1rem' }}>
              <button
                className="load-more-btn"
                onClick={async () => {
                  setIsLoadingMore(true);
                  try {
                    const nextPage = currentPage + 1;
                    const loadParams = {
                      ...filterParams,
                      page: nextPage,
                    };
                    
                    let response;
                    if (searchQuery) {
                      response = await apiService.searchProducts(searchQuery, loadParams);
                    } else {
                      response = await apiService.getProducts(loadParams);
                    }
                    
                    const newProducts = translateProducts(Array.isArray(response.products) ? response.products : []);
                    setAllProducts(prev => [...prev, ...newProducts]);
                    setCurrentPage(nextPage);
                    
                    // Check if there are more products
                    const pagination = response.pagination;
                    const total = response.total || 0;
                    const currentTotal = allProducts.length + newProducts.length;
                    setHasMore(pagination?.hasMore !== false && (pagination?.hasMore || currentTotal < total || newProducts.length >= 20));
                  } catch (err) {
                    console.error('Failed to load more products:', err);
                    setHasMore(false);
                  } finally {
                    setIsLoadingMore(false);
                  }
                }}
                disabled={isLoadingMore}
                style={{
                  padding: '0.75rem 2rem',
                  fontSize: '1rem',
                  fontWeight: 500,
                  color: '#fff',
                  backgroundColor: '#2563eb',
                  border: 'none',
                  borderRadius: '0.5rem',
                  cursor: isLoadingMore ? 'not-allowed' : 'pointer',
                  opacity: isLoadingMore ? 0.7 : 1,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                {isLoadingMore ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    {t('loadingMore', { ns: 'common' })}
                  </>
                ) : (
                  t('loadMore', { ns: 'common' })
                )}
              </button>
            </div>
          )}
        </main>
      </div>

    </div>
  );
};

// Product List Item Component (for List View)
const ProductListItem = ({ product }) => {
  const { t } = useTranslation(['common', 'search']);
  const { price, comparePrice, fromPrice } = getProductPrices(product);
  const discountPercent = comparePrice && comparePrice > price 
    ? Math.round(((comparePrice - price) / comparePrice) * 100) 
    : 0;

  return (
    <div className="product-list-item">
      <Link to={`/product/${getProductId(product)}`} className="list-item-image">
        <img src={getProductImageUrl(product)} alt={getProductName(product)} />
        {discountPercent > 0 && (
          <span className="list-discount-badge">-{discountPercent}%</span>
        )}
      </Link>
      
      <div className="list-item-content">
        <div className="list-item-header">
          <Link to={`/product/${getProductId(product)}`}>
            <h3 className="list-item-name">{getProductName(product)}</h3>
          </Link>
          <button className="list-wishlist-btn">
            <Heart size={20} />
          </button>
        </div>
        
        <div className="list-item-rating">
          <div className="stars">
            {[...Array(5)].map((_, i) => (
              <Star
                key={i}
                size={14}
                fill={i < Math.floor(product.averageRating || product.rating || 4.5) ? '#fbbf24' : 'none'}
                stroke={i < Math.floor(product.averageRating || product.rating || 4.5) ? '#fbbf24' : '#d1d5db'}
              />
            ))}
          </div>
          <span className="rating-text">
            {(product.averageRating || product.rating || 4.5).toFixed(1)} ({product.sold || product.salesCount || 0} {t('sold', { ns: 'common' })})
          </span>
        </div>
        
        <p className="list-item-description">
          {product.description?.substring(0, 150) || t('noDescriptionAvailable', { defaultValue: 'No description available', ns: 'common' })}...
        </p>
        
        <div className="list-item-footer">
          <div className="list-item-prices">
            <span className="list-current-price">
              {fromPrice ? t('fromPrice', { price: formatPrice(price), ns: 'common' }) : formatPrice(price)}
            </span>
            {comparePrice && comparePrice > price && (
              <span className="list-original-price">{formatPrice(comparePrice)}</span>
            )}
          </div>
          
          <div className="list-item-actions">
            <button className="list-add-cart">
              <ShoppingCart size={18} />
              {t('addToCart', { ns: 'common' })}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchPage;
